// src/pages/Contact.jsx
import React, { useState, useRef } from "react";
import SEO from "../components/SEO";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import {
  MapPin,
  Phone,
  Mail,
  Clock,
  ArrowRight,
  Send,
  Instagram,
  ExternalLink,
  MessageCircle,
  ArrowDownLeft,
} from "lucide-react";

/* =========================
   CONSTANTS (align with your real info)
   ========================= */
const CONTACT = {
  phoneDisplay: "+385 95 501 1065",
  phoneE164: "+385955011065",
  email: "d10spizzafantasista@gmail.com",
  instagramHandle: "@d10spizza",
  instagramUrl: "https://www.instagram.com/d10spizza/",
  mapsLabel: "Prosika 51A, 23250 Pag, Croatia",
  mapsUrl:
    "https://www.google.com/maps/search/?api=1&query=Prosika%2051A%2C%2023250%20Pag%2C%20Croatia",
  hoursShort: "Daily: 12:00–23:00",
};

/* --- ANIMATION VARIANTS --- */
const fadeInUp = {
  hidden: { opacity: 0, y: 40 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] },
  },
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1, delayChildren: 0.2 },
  },
};

/* =========================
   HERO
   ========================= */
const ContactHero = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"],
  });

  const y = useTransform(scrollYProgress, [0, 1], ["0%", "20%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1, 1.1]);

  return (
    <header
      ref={containerRef}
      className="relative w-full min-h-[90vh] bg-[#0F172A] overflow-hidden flex flex-col justify-end pb-16 sm:pb-20 pt-28 sm:pt-32 px-4 md:px-8"
    >
      {/* Background (Parallax) */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <motion.div style={{ y, scale }} className="absolute inset-0">
          <img
            src="/img/team/contact.jpeg"
            alt="D10’s atmosphere"
            className="w-full h-full object-cover opacity-80"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0F172A] via-[#0F172A]/65 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0F172A] via-[#0F172A]/45 to-transparent" />
        </motion.div>
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto h-full flex flex-col justify-end">
        {/* Status pill */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.5 }}
          className="absolute top-0 right-0 hidden md:flex items-center gap-2 bg-white/5 backdrop-blur-md border border-white/10 px-4 py-2 rounded-full"
        >
          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse shadow-[0_0_10px_#22c55e]" />
          <span className="font-manrope text-xs font-bold uppercase tracking-widest text-white/85">
            Open today • 12:00–23:00
          </span>
        </motion.div>

        <div className="max-w-[95%] lg:max-w-5xl">
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.75, ease: "easeOut" }}
            className="mb-4"
          >
            <span className="inline-block text-[#0087E6] font-manrope font-bold text-xs md:text-sm uppercase tracking-[0.25em] pl-1">
              Prosika Beach · Pag Island
            </span>
          </motion.div>

          <h1 className="font-anton text-[13vw] sm:text-7xl md:text-8xl lg:text-9xl leading-[0.85] text-white uppercase tracking-tight select-none">
            <motion.span
              initial={{ y: "100%", opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.08, ease: "circOut" }}
              className="block"
            >
              Contact the
            </motion.span>
            <motion.span
              initial={{ y: "100%", opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.8, delay: 0.22, ease: "circOut" }}
              className="block text-transparent bg-clip-text bg-gradient-to-r from-[#0087E6] to-white"
            >
              Squad.
            </motion.span>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45, duration: 0.7 }}
            className="mt-6 font-manrope text-white/70 text-base sm:text-lg leading-relaxed max-w-xl border-l-2 border-[#0087E6] pl-5"
          >
            Reservations, private events, or a quick question. Message us and
            we’ll confirm fast.
          </motion.p>
        </div>

        {/* Bottom grid */}
        <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
          {/* Left note */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.65, duration: 0.8 }}
            className="md:col-span-5 lg:col-span-4"
          >
            <p className="font-manrope text-white/55 text-base sm:text-lg leading-relaxed">
              Coming straight from the beach? Perfect. Want a table for a bigger
              group? Also perfect. Just send a note and we’ll sort it.
            </p>
          </motion.div>

          {/* Glass card */}
          <motion.div
            initial={{ opacity: 0, y: 44 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.8 }}
            className="md:col-span-7 lg:col-span-6 lg:col-start-7 bg-white/10 backdrop-blur-lg border border-white/10 p-6 md:p-8 rounded-3xl"
          >
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
              <div>
                <span className="block text-[10px] font-bold uppercase tracking-widest text-white/50 mb-1">
                  Call the squad
                </span>
                <a
                  href={`tel:${CONTACT.phoneE164}`}
                  className="font-anton text-3xl md:text-4xl text-white hover:text-[#0087E6] transition-colors"
                >
                  {CONTACT.phoneDisplay}
                </a>
                <div className="mt-3 flex items-center gap-2 text-[11px] font-bold uppercase tracking-widest text-white/50">
                  <Clock size={14} className="text-[#FFD700]" />
                  {CONTACT.hoursShort}
                </div>
              </div>

              <div className="flex gap-3">
                <a
                  href={`tel:${CONTACT.phoneE164}`}
                  className="w-12 h-12 rounded-full bg-[#0087E6] text-white flex items-center justify-center hover:bg-white hover:text-[#0F172A] transition-all shadow-lg hover:shadow-[#0087E6]/50"
                  aria-label="Call"
                >
                  <Phone size={20} />
                </a>
                <a
                  href={`mailto:${CONTACT.email}`}
                  className="w-12 h-12 rounded-full bg-white/10 text-white border border-white/20 flex items-center justify-center hover:bg-white hover:text-[#0F172A] transition-all"
                  aria-label="Email"
                >
                  <MessageCircle size={20} />
                </a>
                <a
                  href={CONTACT.mapsUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="w-12 h-12 rounded-full bg-white/10 text-white border border-white/20 flex items-center justify-center hover:bg-white hover:text-[#0F172A] transition-all"
                  aria-label="Open map"
                >
                  <ArrowDownLeft size={20} />
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </header>
  );
};

/* =========================
   INFO GRID
   ========================= */
const InfoGrid = () => {
  const cards = [
    {
      icon: MapPin,
      label: "Find the beach",
      value: "Prosika",
      sub: "Pag Island",
      link: CONTACT.mapsUrl,
    },
    {
      icon: Mail,
      label: "Email the squad",
      value: CONTACT.email,
      sub: "We reply fast",
      link: `mailto:${CONTACT.email}`,
    },
    {
      icon: Instagram,
      label: "Bench cam",
      value: CONTACT.instagramHandle,
      sub: "Daily stories",
      link: CONTACT.instagramUrl,
    },
  ];

  return (
    <section className="relative z-20 px-4 -mt-10 sm:-mt-16 pb-16">
      <div className="container mx-auto">
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-3 gap-4 lg:gap-8"
        >
          {cards.map((card, i) => (
            <motion.a
              key={i}
              variants={fadeInUp}
              href={card.link || "#"}
              target={card.link?.startsWith("http") ? "_blank" : undefined}
              rel={card.link?.startsWith("http") ? "noreferrer" : undefined}
              className="group relative bg-white p-8 rounded-2xl shadow-xl border border-gray-100 overflow-hidden hover:-translate-y-2 hover:shadow-2xl transition-all duration-300"
            >
              <div className="flex items-center justify-between mb-8">
                <div className="w-10 h-10 rounded-full bg-[#F0F9FF] text-[#0087E6] flex items-center justify-center group-hover:bg-[#0087E6] group-hover:text-white transition-colors duration-300">
                  <card.icon size={18} />
                </div>
                <ArrowRight
                  size={18}
                  className="text-gray-300 group-hover:text-[#0087E6] -translate-x-4 opacity-0 group-hover:translate-x-0 group-hover:opacity-100 transition-all duration-300"
                />
              </div>

              <div>
                <span className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-1">
                  {card.label}
                </span>
                <h3 className="font-anton text-2xl text-[#0F172A] uppercase break-words">
                  {card.value}
                </h3>
                <p className="font-manrope text-sm text-gray-500 mt-1">
                  {card.sub}
                </p>

                {card.link?.startsWith("http") && (
                  <div className="mt-4 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#0087E6] opacity-70 group-hover:opacity-100">
                    Open <ExternalLink size={14} />
                  </div>
                )}
              </div>
            </motion.a>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
/* =========================
   CONTACT FORM (WEBHOOK)
   ========================= */
const ContactForm = () => {
  // internal states: idle | submitting | success | error
  const [status, setStatus] = useState("idle");

  // controlled form fields
  const [form, setForm] = useState({
    name: "",
    email: "",
    topic: "Table reservation",
    message: "",
  });

  const WEBHOOK_URL = "https://hook.eu2.make.com/8ko6fn8v1dnkxfcrbyg5q668rwdphn8r";

  const canSubmit =
    form.name.trim() &&
    form.email.trim() &&
    form.message.trim() &&
    status !== "submitting";

  const reset = () => {
    setForm({
      name: "",
      email: "",
      topic: "Table reservation",
      message: "",
    });
    setStatus("idle");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setStatus("submitting");

    // Build payload (clean + useful)
    const payload = {
      source: "d10-website",
      form: "contact",
      page: "/contact",
      submittedAt: new Date().toISOString(),
      contact: {
        name: form.name.trim(),
        email: form.email.trim(),
      },
      topic: form.topic,
      message: form.message.trim(),
      meta: {
        userAgent: navigator?.userAgent || "",
        language: navigator?.language || "",
        timezone:
          Intl?.DateTimeFormat?.().resolvedOptions?.().timeZone || "",
      },
    };

    try {
      const res = await fetch(WEBHOOK_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      // treat non-2xx as failure
      if (!res.ok) throw new Error(`Webhook failed: ${res.status}`);

      setStatus("success");
    } catch (err) {
      // DO NOT show raw error to user
      setStatus("error");
    }
  };

  return (
    <section className="py-16 md:py-24 bg-white">
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto flex flex-col lg:flex-row gap-16 lg:gap-24">
          {/* Left: context */}
          <div className="w-full lg:w-1/3 pt-4">
            <span className="inline-block w-12 h-1 bg-[#0087E6] mb-6" />
            <h2 className="font-anton text-5xl lg:text-6xl text-[#0F172A] uppercase leading-[0.9] mb-6">
              Leave a <br />
              <span className="text-[#0087E6]">note</span>
            </h2>
            <p className="font-manrope text-gray-500 text-lg leading-relaxed mb-8">
              Short or long, doesn’t matter. We’ll read it and reply.
            </p>

            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-3 text-sm font-bold uppercase tracking-widest text-[#0F172A]">
                <Clock size={16} className="text-[#0087E6]" />
                <span>{CONTACT.hoursShort}</span>
              </div>
              <div className="flex items-center gap-3 text-sm font-bold uppercase tracking-widest text-[#0F172A]">
                <MapPin size={16} className="text-[#0087E6]" />
                <span>{CONTACT.mapsLabel}</span>
              </div>
              <div className="flex items-center gap-3 text-sm font-bold uppercase tracking-widest text-[#0F172A]">
                <Mail size={16} className="text-[#0087E6]" />
                <span className="break-words">{CONTACT.email}</span>
              </div>
            </div>
          </div>

          {/* Right: form */}
          <div className="w-full lg:w-2/3">
            <div className="bg-[#F8FAFC] p-8 md:p-12 rounded-[2.5rem]">
              <AnimatePresence mode="wait">
                {status === "success" ? (
                  <motion.div
                    key="success"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="h-full flex flex-col items-center justify-center text-center min-h-[360px]"
                  >
                    <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mb-6">
                      <Send size={32} className="text-green-600" />
                    </div>
                    <h3 className="font-anton text-3xl text-[#0F172A] uppercase">
                      Message received
                    </h3>
                    <p className="text-gray-500 font-manrope mt-2">
                      We’ll get back to you shortly.
                    </p>
                    <button
                      onClick={reset}
                      className="mt-8 text-sm font-bold text-[#0087E6] uppercase tracking-wider hover:text-[#0F172A]"
                    >
                      Send another
                    </button>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onSubmit={handleSubmit}
                    className="space-y-8"
                  >
                    {/* ✅ Friendly error (no “status error”, no raw details) */}
                    {status === "error" && (
                      <div className="rounded-2xl border border-red-200 bg-red-50 p-4">
                        <p className="text-sm font-manrope text-red-700">
                          Couldn’t send right now. Please try again — or email us at{" "}
                          <a
                            className="font-bold underline"
                            href={`mailto:${CONTACT.email}`}
                          >
                            {CONTACT.email}
                          </a>
                          .
                        </p>
                      </div>
                    )}

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                      <div className="group">
                        <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2 group-focus-within:text-[#0087E6] transition-colors">
                          Name
                        </label>
                        <input
                          type="text"
                          required
                          value={form.name}
                          onChange={(e) =>
                            setForm((p) => ({ ...p, name: e.target.value }))
                          }
                          className="w-full bg-transparent border-b-2 border-gray-200 py-3 text-lg font-manrope focus:border-[#0087E6] outline-none transition-colors"
                          placeholder="Your name"
                        />
                      </div>

                      <div className="group">
                        <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2 group-focus-within:text-[#0087E6] transition-colors">
                          Email
                        </label>
                        <input
                          type="email"
                          required
                          value={form.email}
                          onChange={(e) =>
                            setForm((p) => ({ ...p, email: e.target.value }))
                          }
                          className="w-full bg-transparent border-b-2 border-gray-200 py-3 text-lg font-manrope focus:border-[#0087E6] outline-none transition-colors"
                          placeholder="email@address.com"
                        />
                      </div>
                    </div>

                    <div className="group">
                      <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2 group-focus-within:text-[#0087E6] transition-colors">
                        What’s it about?
                      </label>
                      <select
                        value={form.topic}
                        onChange={(e) =>
                          setForm((p) => ({ ...p, topic: e.target.value }))
                        }
                        className="w-full bg-transparent border-b-2 border-gray-200 py-3 text-lg font-manrope focus:border-[#0087E6] outline-none transition-colors"
                      >
                        <option>Table reservation</option>
                        <option>Private event</option>
                        <option>Big group</option>
                        <option>Something else</option>
                      </select>
                    </div>

                    <div className="group">
                      <label className="block text-[10px] font-bold uppercase tracking-widest text-gray-400 mb-2 group-focus-within:text-[#0087E6] transition-colors">
                        Message
                      </label>
                      <textarea
                        required
                        rows={4}
                        value={form.message}
                        onChange={(e) =>
                          setForm((p) => ({ ...p, message: e.target.value }))
                        }
                        className="w-full bg-transparent border-b-2 border-gray-200 py-3 text-lg font-manrope focus:border-[#0087E6] outline-none transition-colors resize-none"
                        placeholder="Dates, time, number of people… or just ‘we want pizza’."
                      />
                      <p className="mt-2 text-[11px] text-gray-400 font-manrope">
                        Tip: include a day + number of people for reservations.
                      </p>
                    </div>

                    <div className="pt-2">
                      <button
                        type="submit"
                        disabled={!canSubmit}
                        className="inline-flex items-center gap-3 px-8 py-4 bg-[#0F172A] text-white font-anton uppercase text-lg tracking-widest rounded-full hover:bg-[#0087E6] transition-all duration-300 shadow-lg hover:shadow-xl hover:-translate-y-1 active:scale-95 disabled:opacity-70 disabled:cursor-not-allowed"
                      >
                        {status === "submitting" ? (
                          "Sending..."
                        ) : (
                          <>
                            Send <ArrowRight size={20} />
                          </>
                        )}
                      </button>
                    </div>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* =========================
   MAP FOOTER
   ========================= */
const MapFooter = () => (
  <section className="w-full h-[60vh] min-h-[450px] relative grayscale hover:grayscale-0 transition-all duration-700">
    <iframe
      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d11397.641887132912!2d15.0506307!3d44.4429399!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x476237004f26b52d%3A0x6e3e5b1a0a5b2e5a!2sProsika%20Beach!5e0!3m2!1sen!2shr!4v1700000000000!5m2!1sen!2shr"
      width="100%"
      height="100%"
      style={{ border: 0 }}
      allowFullScreen=""
      loading="lazy"
      referrerPolicy="no-referrer-when-downgrade"
      title="D10’s location map"
    />
    <div className="absolute inset-0 pointer-events-none shadow-[inset_0_20px_60px_rgba(0,0,0,0.12)]" />

    <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
      <div className="w-24 h-24 bg-[#0087E6]/20 rounded-full flex items-center justify-center animate-ping absolute opacity-75" />
      <div className="w-24 h-24 bg-[#0087E6]/90 rounded-full flex items-center justify-center shadow-2xl relative">
        <MapPin size={32} className="text-white" />
      </div>
    </div>
  </section>
);

/* =========================
   STYLES
   ========================= */
const GlobalStyles = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Anton&family=Manrope:wght@400;500;700;800&display=swap');
    .font-anton { font-family: 'Anton', sans-serif; }
    .font-manrope { font-family: 'Manrope', sans-serif; }
    ::selection { background: #0087E6; color: white; }
    html { scroll-behavior: smooth; -webkit-tap-highlight-color: transparent; }
    body { overflow-x: hidden; }
    button, a { min-height: 44px; }
    :focus-visible { outline: 2px solid #0087E6; outline-offset: 2px; }
  `}</style>
);

/* =========================
   PAGE
   ========================= */
export default function Contact() {
  return (
    <div className="bg-white min-h-screen w-full overflow-x-hidden flex flex-col">
      <SEO
        title="Contact | D10’s Pizza Fantasista"
        description="Reservations, private events, or a quick question. Find us on Prosika Beach, Pag."
        image="/og-contact.png"
        canonical="https://www.d10s.pizza/contact"
        type="website"
      />

      <GlobalStyles />

      <Navbar />

      <main className="flex-grow">
        <ContactHero />
        <InfoGrid />
        <ContactForm />
        <MapFooter />
      </main>

      <Footer />
    </div>
  );
}
